/**
 * ExpedienteSidebar — Columna lateral sticky de la pestaña Expediente.
 *
 * 3 tarjetas:
 *   1. Resumen        → Aseguradora, Estado, Causa, Fecha encargo,
 *                       Fecha siniestro, Reserva, Perito.
 *   2. Contacto       → WhatsApp (asegurado_telefono) + Teléfono fijo
 *                       (asegurado_movil) cada uno con su icono y enlace.
 *   3. Pendientes     → Lista numerada con las acciones que el perito tiene
 *                       que cerrar (derivado del estado del expediente +
 *                       incidencias/ampliaciones sin respuesta).
 *
 * En pantallas estrechas (lg-) se apila debajo del contenido principal;
 * en lg+ se queda sticky a 8px del top del scroller del expediente.
 */
import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import CitaNotificacionCard from "@/components/expediente/CitaNotificacionCard";
import AseguradoActivityCard from "@/components/expediente/AseguradoActivityCard";
import SolicitudesDocCard from "@/components/expediente/SolicitudesDocCard";
import {
  ChatCircleDots, Phone, ArrowSquareOut, Buildings, Calendar,
  CurrencyEur, UserCircle, ClipboardText, Warning, ArrowsOut,
  CheckSquare, Tag,
} from "@phosphor-icons/react";

/* -------------------------- Helpers de formato -------------------------- */
const fmtDate = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (isNaN(d)) return iso;
    return d.toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "2-digit" });
  } catch { return iso; }
};
const fmtEUR = (n) => {
  if (n == null || n === 0) return null;
  return new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);
};

/* -------------------------- 1. Tarjeta Resumen ------------------------- */
function ResumenCard({ exp, data, peritos = [] }) {
  const aseguradora = data.aseguradora || exp.detected_aseguradora || "—";
  const causa = data.causa || "—";
  const fEncargo = fmtDate(data.fecha_encargo) || "—";
  const fSiniestro = fmtDate(data.fecha_siniestro) || "—";
  const reserva = fmtEUR(exp.importe_reserva) || "—";
  const perito = peritos.find((p) => p.code === data.perito_codigo);
  const peritoLabel = perito ? `${perito.code} — ${perito.name}` : (data.perito_codigo || "— Sin asignar —");

  return (
    <section
      className="bg-white border border-border rounded-lg overflow-hidden"
      data-testid="sidebar-resumen-card"
    >
      <header className="flex items-center gap-2 px-4 py-2.5 border-b border-border/70">
        <span className="icon-chip chip-icon-amber flex-shrink-0"><ClipboardText className="w-4 h-4" weight="regular" /></span>
        <h3 className="section-title">Resumen</h3>
      </header>
      <dl className="px-4 py-3 text-[12px] space-y-2" data-testid="sidebar-resumen-body">
        <Row icon={Buildings} label="Aseguradora" value={aseguradora} />
        <Row icon={CheckSquare} label="Estado" valueNode={<StatusBadge status={exp.status} />} />
        <Row icon={Tag} label="Causa" value={causa} />
        <Row icon={Calendar} label="F. encargo" value={fEncargo} mono />
        <Row icon={Calendar} label="F. siniestro" value={fSiniestro} mono />
        <Row icon={CurrencyEur} label="Reserva" value={reserva} mono className={exp.reserva_supera_limite ? "text-status-error-fg font-semibold" : ""} />
        <Row icon={UserCircle} label="Perito" value={peritoLabel} />
      </dl>
    </section>
  );
}

function Row({ icon: Icon, label, value, valueNode, mono = false, className = "" }) {
  return (
    <div className="grid grid-cols-[16px_88px_1fr] items-start gap-2">
      <Icon className="w-3.5 h-3.5 text-muted mt-[2px]" weight="regular" />
      <dt className="text-muted text-[11px] uppercase tracking-wide">{label}</dt>
      <dd className={`text-foreground ${mono ? "font-mono" : ""} ${className} truncate`} title={typeof value === "string" ? value : undefined}>
        {valueNode != null ? valueNode : (value || <span className="text-muted italic">—</span>)}
      </dd>
    </div>
  );
}

/* -------------------------- 2. Tarjeta Contacto ----------------------- */
function ContactoCard({ exp }) {
  const ed = exp?.extracted_data || {};
  const whatsapp = ed.asegurado_telefono;
  const fijo = ed.asegurado_movil;
  const [loading, setLoading] = useState(false);

  const openWhatsapp = async () => {
    if (!whatsapp) { toast.error("Falta el teléfono del asegurado en el expediente."); return; }
    setLoading(true);
    try {
      const r = await api.get(`/expedientes/${exp.id}/chatwoot/conversation`);
      const url = r.data?.conversation_url;
      if (url) window.open(url, "_blank", "noopener,noreferrer");
      else toast.error("No se pudo abrir la conversación.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error abriendo la conversación");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      className="bg-white border border-border rounded-lg overflow-hidden"
      data-testid="sidebar-contacto-card"
    >
      <header className="flex items-center gap-2 px-4 py-2.5 border-b border-border/70">
        <span className="icon-chip chip-icon-teal flex-shrink-0"><ChatCircleDots className="w-4 h-4" weight="regular" /></span>
        <h3 className="section-title">Contacto</h3>
      </header>
      <div className="px-4 py-3 space-y-2.5 text-[12px]">
        {/* WhatsApp */}
        <ContactRow
          Icon={ChatCircleDots}
          label="WhatsApp"
          value={whatsapp}
          action={whatsapp ? (loading ? "Abriendo…" : "Abrir") : null}
          onAction={openWhatsapp}
          ariaLabel="Abrir conversación de WhatsApp en Chatwoot"
          testid="sidebar-contacto-whatsapp"
          tone="teal"
        />
        {/* Teléfono fijo / móvil */}
        <ContactRow
          Icon={Phone}
          label="Teléfono"
          value={fijo}
          action={fijo ? "Llamar" : null}
          href={fijo ? `tel:${fijo.replace(/\s+/g, "")}` : null}
          ariaLabel="Marcar teléfono del asegurado"
          testid="sidebar-contacto-telefono"
          tone="blue"
        />
      </div>
    </section>
  );
}

function ContactRow({ Icon, label, value, action, onAction, href, ariaLabel, testid, tone }) {
  const toneCls = tone === "teal" ? "text-accent-teal-fg" : "text-accent-blue-fg";
  return (
    <div className="flex items-center justify-between gap-2" data-testid={testid}>
      <div className="flex items-center gap-2 min-w-0">
        <Icon className={`w-4 h-4 flex-shrink-0 ${toneCls}`} weight="regular" />
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wide text-muted">{label}</div>
          <div className={`text-[13px] truncate ${value ? "text-foreground font-medium" : "text-muted italic"}`}>
            {value || "sin número"}
          </div>
        </div>
      </div>
      {action && (
        href ? (
          <a
            href={href}
            aria-label={ariaLabel}
            data-testid={`${testid}-action`}
            className="text-[11.5px] font-semibold border border-border bg-white text-foreground hover:bg-background rounded-md px-2.5 h-7 inline-flex items-center gap-1 transition flex-shrink-0"
          >
            <ArrowSquareOut className="w-3 h-3" weight="regular" /> {action}
          </a>
        ) : (
          <button
            type="button"
            onClick={onAction}
            aria-label={ariaLabel}
            data-testid={`${testid}-action`}
            className="text-[11.5px] font-semibold border border-border bg-white text-foreground hover:bg-background rounded-md px-2.5 h-7 inline-flex items-center gap-1 transition flex-shrink-0"
          >
            <ArrowSquareOut className="w-3 h-3" weight="regular" /> {action}
          </button>
        )
      )}
    </div>
  );
}

/* -------------------------- 3. Tarjeta Pendientes ---------------------- */
function PendientesCard({ exp, data }) {
  // Derivamos las gestiones pendientes del estado del expediente.
  const items = [];
  if (!data.perito_codigo) items.push("Asignar perito al expediente.");
  if (!exp.fecha_contacto) items.push("Contactar con el asegurado.");
  if (!exp.fecha_visita) items.push("Programar visita.");
  else if (!exp.visita_realizada) items.push(`Marcar visita como realizada (programada ${fmtDate(exp.fecha_visita)}).`);
  if (!exp.fecha_preliminar) items.push("Emitir Informe Preliminar (IP).");

  const incidenciasPend = (exp.incidencias || []).filter((x) => x.tipo === "incidencia" && !x.respuesta).length;
  const ampliacionesPend = (exp.incidencias || []).filter((x) => x.tipo === "ampliacion" && !x.respuesta).length;
  if (incidenciasPend > 0) items.push(`Responder ${incidenciasPend} incidencia${incidenciasPend === 1 ? "" : "s"}.`);
  if (ampliacionesPend > 0) items.push(`Responder ${ampliacionesPend} ampliación${ampliacionesPend === 1 ? "" : "es"}.`);

  if (exp.fecha_preliminar && !exp.fecha_cierre) items.push("Finalizar expediente (Inf. Final).");

  return (
    <section
      className="bg-white border border-border rounded-lg overflow-hidden"
      data-testid="sidebar-pendientes-card"
    >
      <header className="flex items-center justify-between gap-2 px-4 py-2.5 border-b border-border/70">
        <div className="flex items-center gap-2 min-w-0">
          <span className="icon-chip chip-icon-yellow flex-shrink-0"><Warning className="w-4 h-4" weight="regular" /></span>
          <h3 className="section-title">Gestiones pendientes</h3>
        </div>
        {items.length > 0 && (
          <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-status-warning-bg text-status-warning-fg text-[11px] font-bold tabular-nums" data-testid="sidebar-pendientes-count">
            {items.length}
          </span>
        )}
      </header>
      <div className="px-4 py-3">
        {items.length === 0 ? (
          <div className="text-[12px] text-status-success-fg inline-flex items-center gap-1.5 font-medium">
            <CheckSquare className="w-4 h-4" weight="fill" />
            ¡Nada pendiente!
          </div>
        ) : (
          <ol className="text-[12px] space-y-1.5 list-none" data-testid="sidebar-pendientes-list">
            {items.map((t, i) => (
              <li key={t} className="flex gap-2 items-start">
                <span className="text-[10.5px] font-bold text-muted tabular-nums mt-[1px] w-4 flex-shrink-0">{i + 1}.</span>
                <span className="text-foreground">{t}</span>
              </li>
            ))}
          </ol>
        )}
      </div>
    </section>
  );
}

/* -------------------------- Sidebar contenedor ----------------------- */
export default function ExpedienteSidebar({ exp, data, peritos = [], onOpenVisita }) {
  return (
    <aside
      data-testid="expediente-sidebar"
      className="w-full lg:w-[330px] lg:flex-shrink-0 space-y-5 lg:sticky lg:top-0 lg:self-start"
    >
      <ResumenCard exp={exp} data={data} peritos={peritos} />
      <ContactoCard exp={exp} />
      <PendientesCard exp={exp} data={data} />
      <CitaNotificacionCard exp={exp} onOpenVisita={onOpenVisita} />
      <SolicitudesDocCard expediente={exp} />
      <AseguradoActivityCard expedienteId={exp.id} />
    </aside>
  );
}
